import { DEFAULT_CODE_LANGUAGE } from "@/components/code-take-card"
import type { Platform, Story, SyndicationClassName } from "@/types"

export const MAX_ALT_TEXT_LENGTH = 125
export const DEFAULT_TAKE_TYPE = "Take"
export const CODE_TAKE_TYPE = "CodeTake"
export const QUOTE_TAKE_TYPE = "QuoteTake"

export interface TakeMediaFileAttribute {
  id?: number
  media_file?: File
  alt_text?: string
  _destroy?: boolean
}

export type TakeMediaFilesAttributes = Record<number, TakeMediaFileAttribute>

export interface StoryTakeAttribute {
  id?: number
  type: "Take" | "CodeTake" | "QuoteTake"
  body: string
  code_snippet: string
  code_language: string
  code_image_data_url: string
  quoted_take_id?: number | null
  position: number
  _destroy?: boolean
  take_media_files_attributes: TakeMediaFilesAttributes
}

export type StoryTakesAttributes = Record<number, StoryTakeAttribute>

export interface SyndicationAttribute {
  id?: number
  type: SyndicationClassName
  platform_id: number
  platform_credential_id?: number | null
  youtube_category_id?: number | null
  facebook_page_id?: number | null
  pinterest_board_id?: number | null
  _destroy?: boolean
}

export type StorySyndicationsAttributes = Record<number, SyndicationAttribute>

// A story can target several accounts on one platform, so a delivery is identified
// by its connected account. Platforms that need no credential (dev-only test stubs)
// fall back to the platform name.
export function syndicationEntryKey(
  platformName: string,
  platformCredentialId?: number | null,
): string {
  return platformCredentialId != null
    ? `credential:${platformCredentialId}`
    : `platform:${platformName}`
}

// The platforms a story actually publishes to are the ones with a live
// syndication (not marked for destruction). A story can target multiple accounts
// on one platform, so de-duplicate by platform id and keep `availablePlatforms`
// ordering for a stable icon row.
export function selectedPlatformsFromSyndications(
  syndicationsAttributes: StorySyndicationsAttributes,
  availablePlatforms: Platform[],
): Platform[] {
  const selectedPlatformIds = new Set(
    Object.values(syndicationsAttributes)
      .filter((attr) => !attr._destroy)
      .map((attr) => attr.platform_id),
  )
  return availablePlatforms.filter((platform) =>
    selectedPlatformIds.has(platform.id),
  )
}

export function syndicationsAttributesFromStory(
  story: Story,
): StorySyndicationsAttributes {
  return story.syndications.reduce<StorySyndicationsAttributes>(
    (attributes, syndication, index) => ({
      ...attributes,
      [index]: {
        id: syndication.id,
        type: syndication.platform.syndication_class_name,
        platform_id: syndication.platform.id,
        platform_credential_id: syndication.platform_credential_id ?? null,
        youtube_category_id: syndication.youtube_category_id ?? null,
        facebook_page_id: syndication.facebook_page_id ?? null,
        pinterest_board_id: syndication.pinterest_board_id ?? null,
      },
    }),
    {},
  )
}

export interface StoryFormData {
  title?: string
  tags?: string[]
  scheduled_at?: string
  syndications_attributes: StorySyndicationsAttributes
  takes_attributes: StoryTakesAttributes
}

export function isVideoContentType(contentType?: string | null) {
  return contentType?.startsWith("video/") ?? false
}

export function imageFilesFromClipboard(clipboardData: DataTransfer): File[] {
  const files = Array.from(clipboardData.files)
  if (files.length === 0) {
    for (const item of Array.from(clipboardData.items)) {
      if (item.kind === "file") {
        const file = item.getAsFile()
        if (file) files.push(file)
      }
    }
  }
  return files.filter((file) => file.type.startsWith("image/"))
}

export function isCodeTake(
  take: Pick<StoryTakeAttribute, "type"> | { type: string },
) {
  return take.type === CODE_TAKE_TYPE
}

export function isQuoteTake(
  take: Pick<StoryTakeAttribute, "type"> | { type: string },
) {
  return take.type === QUOTE_TAKE_TYPE
}

export function sortVisibleTakes(entries: [number, StoryTakeAttribute][]) {
  return entries
    .filter(([, take]) => !take._destroy)
    .sort((a, b) => a[1].position - b[1].position)
}

export function normalizeTakePositions(takesAttributes: StoryTakesAttributes) {
  const visibleEntries = sortVisibleTakes(
    Object.entries(takesAttributes).map(([key, take]) => [Number(key), take]),
  )

  const next = { ...takesAttributes }
  visibleEntries.forEach(([key, take], index) => {
    next[key] = { ...take, position: index }
  })

  return next
}

export function mediaFileCompositeKey(
  takeKey: number,
  mediaKey: number | string,
) {
  return `${takeKey}:${mediaKey}`
}

export function existingMediaCompositeKey(takeKey: number, mediaId: number) {
  return `existing:${takeKey}:${mediaId}`
}

export function emptyTakeAttributes(
  type: StoryTakeAttribute["type"] = DEFAULT_TAKE_TYPE,
) {
  return {
    type,
    body: "",
    code_snippet: "",
    code_language: DEFAULT_CODE_LANGUAGE,
    code_image_data_url: "",
    quoted_take_id: null,
    take_media_files_attributes: {},
  }
}
